/**
 * IndexedDB outbox for livescore event mutations (create/update/delete).
 * Rows stay until HTTP flush succeeds; survives refresh.
 */

export type OutboxOp = 'create' | 'update' | 'delete';

export type OutboxStatus = 'pending' | 'flushing';

export interface OutboxRecord {
    /** Client uuid primary key. */
    id: string;
    matchId: number;
    op: OutboxOp;
    /** JSON-serializable Create/Update body or { eventId } for delete. */
    payload: unknown;
    /** Stable local id used in the UI before server event.id exists. */
    clientEventKey: string;
    serverEventId?: number;
    label: string;
    createdAt: number;
    status: OutboxStatus;
    /** Set when status becomes flushing; used to recover hung attempts. */
    flushStartedAt?: number;
    /**
     * After a hung/failed flush, next attempt should GET+fingerprint before
     * create POST so we do not double-submit.
     */
    needsReconcile?: boolean;
    /**
     * Failed flush count (timeout / HTTP error / stuck). After MAX_AUTO_ATTEMPTS
     * the queue holds the row for Sync now.
     */
    attempts?: number;
    /** Snapshot of the optimistic EventVm for rehydrate after refresh. */
    optimisticEvent?: unknown;
}

const DB_NAME = 'twa-arena';
const DB_VERSION = 1;
const STORE = 'eventOutbox';

const openDb = (): Promise<IDBDatabase> =>
    new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = (): void => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE)) {
                const store = db.createObjectStore(STORE, { keyPath: 'id' });
                store.createIndex('matchId', 'matchId', { unique: false });
                store.createIndex('status', 'status', { unique: false });
                store.createIndex('createdAt', 'createdAt', { unique: false });
            }
        };
        req.onsuccess = (): void => resolve(req.result);
        req.onerror = (): void => reject(req.error ?? new Error('IndexedDB open failed'));
    });

const withStore = async <T>(
    mode: IDBTransactionMode,
    fn: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> => {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const store = tx.objectStore(STORE);
        const req = fn(store);
        req.onsuccess = (): void => resolve(req.result);
        req.onerror = (): void => reject(req.error ?? new Error('IndexedDB request failed'));
    });
};

export const newOutboxId = (): string =>
    `ob-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const newClientEventKey = (): string =>
    `cek-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/** Stable negative id so React keys / sort stay unique before server id exists. */
export const tempEventIdFromKey = (clientEventKey: string): number => {
    let h = 0;
    for (let i = 0; i < clientEventKey.length; i += 1) {
        h = (h * 31 + clientEventKey.charCodeAt(i)) | 0;
    }
    const n = Math.abs(h) % 1_000_000_000;
    return n === 0 ? -1 : -n;
};

export const offlineEventStore = {
    add: async (record: OutboxRecord): Promise<void> => {
        await withStore('readwrite', (store) => store.put(record));
    },

    update: async (record: OutboxRecord): Promise<void> => {
        await withStore('readwrite', (store) => store.put(record));
    },

    remove: async (id: string): Promise<void> => {
        await withStore('readwrite', (store) => store.delete(id));
    },

    get: async (id: string): Promise<OutboxRecord | undefined> => {
        const row = await withStore('readonly', (store) => store.get(id));
        return row as OutboxRecord | undefined;
    },

    listAll: async (): Promise<OutboxRecord[]> => {
        const rows = await withStore('readonly', (store) => store.getAll());
        return ((rows as OutboxRecord[]) || []).sort((a, b) => a.createdAt - b.createdAt);
    },

    listByMatch: async (matchId: number): Promise<OutboxRecord[]> => {
        const all = await offlineEventStore.listAll();
        return all.filter((r) => r.matchId === matchId);
    },

    listPending: async (): Promise<OutboxRecord[]> => {
        const all = await offlineEventStore.listAll();
        return all.filter((r) => r.status === 'pending' || r.status === 'flushing');
    },

    /** After crash: anything left flushing becomes pending again. */
    resetFlushingToPending: async (): Promise<void> => {
        const all = await offlineEventStore.listAll();
        await Promise.all(
            all
                .filter((r) => r.status === 'flushing')
                .map((r) =>
                    offlineEventStore.update({
                        ...r,
                        status: 'pending',
                        flushStartedAt: undefined,
                        needsReconcile: r.op === 'create' ? true : r.needsReconcile,
                    })
                )
        );
    },

    findByClientEventKey: async (clientEventKey: string): Promise<OutboxRecord | undefined> => {
        const all = await offlineEventStore.listAll();
        return all.find((r) => r.clientEventKey === clientEventKey);
    },
};
